import { useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions } from 'entities';

import { Column, Row, IconButton, Button, Input, ResponsiveOverlay } from 'components/Blocks';
import { SaveIcon, XIcon } from 'components/Icons';

import { useToast } from 'providers/Toast';
import { useWatch } from 'hooks';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { isNumericText } from 'utils/text';

interface TicketModalProps {
  children?: never;
  close: () => void;
  on: boolean;
}

export function TicketModal({ close, on }: TicketModalProps) {
  const { t } = useLang();
  const { addToast } = useToast();
  const { project, updateProject } = useProjectProfile();

  const tickets = useMemo(
    () => project.invoice.freshDeskTickets || [],
    [project.invoice.freshDeskTickets],
  );

  const [newTicket, setNewTicket] = useState('');

  const [allTickets, setTickets] = useState(tickets);

  useWatch(tickets, () => setTickets(tickets));

  const canSave =
    !!allTickets.length &&
    (tickets.length !== allTickets.length ||
      tickets.some((ticket) => !allTickets.find((tkt) => tkt === ticket)));

  const removeTicket = (ticket: string) => {
    setTickets((old) => old.filter((existing) => existing !== ticket));
  };

  const addTicket = ({ key }: { key: string }) => {
    if (['Enter', ' '].every((okKey) => okKey !== key)) return;

    const toAdd = newTicket.trim();

    if (!toAdd.length) return;

    setNewTicket('');

    if (allTickets.includes(toAdd)) return;

    setTickets((old) => [...old, toAdd]);
  };

  const updateTickets = actions.invoice.updateProjectFreshDeskTickets.use({
    block: !canSave,

    params: {
      tickets: allTickets,
      project: project.id,
    },

    onSuccess: (tickets) => {
      if (allTickets.length !== tickets.length)
        addToast({
          title: t('some-tickets-sent-could-not-be-saved'),
          description: t('these-tickets-are-associated-with-other-projects-or-not-valid')!,
        });

      updateProject((old) => ({
        ...old,

        invoice: {
          ...old.invoice,

          freshDeskTickets: tickets,
        },
      }));
    },
  });

  const onClose = () => {
    setNewTicket('');
    setTickets(tickets);
    close();
  };

  return (
    <ResponsiveOverlay
      default="sheet"
      className="overflow-y-auto"
      close={onClose}
      isOpen={on}
      title={t('freshdesk-tickets')}
    >
      {allTickets.map((ticket) => (
        <Row gap="2" key={ticket}>
          <Input value={ticket} disabled />

          <IconButton onClick={() => removeTicket(ticket)}>
            <XIcon size={14} />
          </IconButton>
        </Row>
      ))}

      <Column gap="2">
        <Row justifyContent="space-between">
          <span>{t('add-new-ticket')}</span>

          <span className="text-end text-xs italic">{t('press-enter-or-space-to-add')}</span>
        </Row>

        <Input
          value={newTicket}
          onChange={({ target }) => isNumericText(target.value) && setNewTicket(target.value)}
          onKeyDown={addTicket}
        />
      </Column>

      <Button disabled={!canSave} {...updateTickets.buttonProps}>
        <SaveIcon />
        {t('save')}
      </Button>
    </ResponsiveOverlay>
  );
}
